import { useEffect, useRef, useState } from 'react'
import { getAllDrafts, getSubmissions, deleteSubmission } from '@/lib/db'
import { getMySubmissions } from '@/lib/api'
import { getAuthStateSnapshot } from '@/lib/authStatus'
import { isVisibleToUser } from '@/lib/userScope'
import { parseDateToTimestamp } from '@/lib/time'
import { UPLOAD_PROGRESS_EVENT, type UploadProgressEventDetail } from '@/lib/uploadEvents'
import type { DraftRecording, Submission } from '@/types'

type Recording = DraftRecording | Submission

const PAGE_SIZE = 100

// A submission whose soft-delete window has elapsed has been hard-deleted
// server-side; any local copy is stale and should be purged so it stops showing.
function isDeletionExpired(submission: Submission): boolean {
  if (submission.deletionStatus !== 'pending' || !submission.deletionExpiresAt) {
    return false
  }
  const expiry = new Date(submission.deletionExpiresAt).getTime()
  return !Number.isNaN(expiry) && expiry <= Date.now()
}

async function loadLocalById(currentUserId: string | null): Promise<Map<string, Recording>> {
  const map = new Map<string, Recording>()
  let drafts: DraftRecording[] = []
  let submissions: Submission[] = []
  try {
    drafts = await getAllDrafts()
  } catch (err) {
    console.warn('Failed to load drafts:', err)
  }
  try {
    submissions = await getSubmissions()
  } catch (err) {
    console.warn('Failed to load submissions:', err)
  }
  // Submission rows win over drafts on id overlap (authoritative upload status),
  // matching useRecordingListLoader. Rows belonging to a different account are
  // hidden (IndexedDB persists across logout on a shared browser).
  for (const draft of drafts) {
    if (isVisibleToUser(draft.userId, currentUserId)) map.set(draft.id, draft)
  }
  for (const submission of submissions) {
    if (!isVisibleToUser(submission.userId, currentUserId)) continue
    if (isDeletionExpired(submission)) {
      void deleteSubmission(submission.id)
      continue
    }
    map.set(submission.id, submission)
  }
  return map
}

// Merge AWS rows under the local rows: a row present on-device keeps its local
// copy (which carries the audio blob and freshest status), while AWS supplies
// everything not held locally. De-dup is by stable submission id.
function mergeAndSort(local: Map<string, Recording>, remote: Submission[]): Recording[] {
  const merged = new Map<string, Recording>(local)
  for (const item of remote) {
    if (!merged.has(item.id)) {
      merged.set(item.id, item)
    }
  }
  return Array.from(merged.values()).sort(
    (a, b) => parseDateToTimestamp(b.createdAt) - parseDateToTimestamp(a.createdAt),
  )
}

/**
 * Owns the "My Submissions" history list: local drafts + submissions merged with
 * the user's AWS-sourced submissions, paginated. Stays usable offline (renders
 * the local rows and flags the degraded state). Pauses local re-merges while a
 * clip is playing so existing audio elements aren't torn down mid-playback.
 */
export function useMySubmissionsLoader() {
  const [recordings, setRecordings] = useState<Recording[]>([])
  const [localIds, setLocalIds] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isOffline, setIsOffline] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [currentPlayingId, setCurrentPlayingId] = useState<string | null>(null)

  const localMapRef = useRef<Map<string, Recording>>(new Map())
  const remoteAccumRef = useRef<Submission[]>([])
  const lastKeyRef = useRef<string | undefined>(undefined)
  const playbackLockedRef = useRef(false)
  const pendingRefreshRef = useRef(false)
  const hasLoadedOnceRef = useRef(false)

  const applyMerged = () => {
    setRecordings(mergeAndSort(localMapRef.current, remoteAccumRef.current))
  }

  const reloadLocal = async () => {
    localMapRef.current = await loadLocalById(getAuthStateSnapshot().userSub)
    setLocalIds(new Set(localMapRef.current.keys()))
  }

  const load = async () => {
    if (!hasLoadedOnceRef.current) {
      setIsLoading(true)
    }
    setError(null)

    // Local first — always available, even offline.
    await reloadLocal()

    // A full (re)load restarts remote paging from the first page.
    remoteAccumRef.current = []
    lastKeyRef.current = undefined
    setHasMore(false)

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setIsOffline(true)
    } else {
      try {
        const page = await getMySubmissions({ limit: PAGE_SIZE })
        remoteAccumRef.current = page.submissions ?? []
        lastKeyRef.current = page.lastKey
        setHasMore(Boolean(page.lastKey))
        setIsOffline(false)
      } catch (err) {
        // Keep the local rows; surface a soft degraded state rather than erroring.
        console.warn('Failed to load submissions from server:', err)
        setIsOffline(true)
      }
    }

    applyMerged()
    hasLoadedOnceRef.current = true
    setIsLoading(false)
  }

  const loadMore = async () => {
    if (isLoadingMore || !lastKeyRef.current) {
      return
    }
    setIsLoadingMore(true)
    try {
      const page = await getMySubmissions({ limit: PAGE_SIZE, lastKey: lastKeyRef.current })
      remoteAccumRef.current = [...remoteAccumRef.current, ...(page.submissions ?? [])]
      lastKeyRef.current = page.lastKey
      setHasMore(Boolean(page.lastKey))
      setIsOffline(false)
      applyMerged()
    } catch (err) {
      console.warn('Failed to load more submissions:', err)
      setError('Failed to load more. Please try again.')
    } finally {
      setIsLoadingMore(false)
    }
  }

  // Re-read local rows (no network) and re-merge with the already-fetched AWS
  // pages, unless a clip is playing — then defer until playback stops.
  const refreshLocal = async () => {
    if (playbackLockedRef.current) {
      pendingRefreshRef.current = true
      return
    }
    await reloadLocal()
    applyMerged()
  }

  const handlePlaybackStart = (id: string) => {
    playbackLockedRef.current = true
    setCurrentPlayingId(id)
  }

  const handlePlaybackStop = (id: string) => {
    setCurrentPlayingId((prev) => (prev === id ? null : prev))
    if (currentPlayingId && currentPlayingId !== id) {
      return
    }
    playbackLockedRef.current = false
    if (pendingRefreshRef.current) {
      pendingRefreshRef.current = false
      void refreshLocal()
    }
  }

  useEffect(() => {
    void load()
  }, [])

  // A save/delete elsewhere only changes local rows; re-merge without refetching.
  useEffect(() => {
    const handleStorageChange = () => {
      void refreshLocal()
    }
    window.addEventListener('recording-saved', handleStorageChange)
    return () => {
      window.removeEventListener('recording-saved', handleStorageChange)
    }
  }, [])

  useEffect(() => {
    const handleUploadProgress = (event: Event) => {
      const detail = (event as CustomEvent<UploadProgressEventDetail>).detail
      if (!detail) {
        return
      }
      if (detail.type === 'refresh' || (detail.type === 'complete' && detail.total > 0)) {
        void refreshLocal()
      }
    }
    window.addEventListener(UPLOAD_PROGRESS_EVENT, handleUploadProgress)
    return () => {
      window.removeEventListener(UPLOAD_PROGRESS_EVENT, handleUploadProgress)
    }
  }, [])

  return {
    recordings,
    localIds,
    isLoading,
    isLoadingMore,
    error,
    isOffline,
    hasMore,
    loadMore,
    reload: load,
    refreshLocal,
    currentPlayingId,
    handlePlaybackStart,
    handlePlaybackStop,
  }
}
